import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Page,
  LayoutSingleColumn,
  NamedLink,
  H1,
  H2,
  H3,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import css from './SitemapPage.module.css';

const SitemapPageComponent = props => {
  const { scrollingDisabled, intl } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  const title = intl.formatMessage({ id: 'SitemapPage.title' });
  const description = intl.formatMessage({ id: 'SitemapPage.description' });

  // Get public routes from route configuration
  const getPublicRoutes = () => {
    // Paths to exclude from the sitemap (pages that exist but shouldn't be listed)
    const excludedPaths = ['/styleguide', '/ai-demo', '/sitemap', '/u', '/confirm'];

    const publicRoutes = routeConfiguration.filter(route => {
      // Filter out routes that require authentication or are internal
      const isPublic = !route.auth &&
                      !excludedPaths.includes(route.path) &&
                      !route.path.includes('checkout') &&
                      !route.path.includes('edit') &&
                      !route.path.includes('new') &&
                      !route.path.includes('draft') &&
                      !route.path.includes('pending-approval') &&
                      !route.path.includes('inbox') &&
                      !route.path.includes('order') &&
                      !route.path.includes('sale') &&
                      !route.path.includes('listings') &&
                      !route.path.includes('account') &&
                      !route.path.includes('profile-settings') &&
                      !route.path.includes('reset-password') &&
                      !route.path.includes('verify-email') &&
                      !route.path.includes('preview') &&
                      !route.path.includes('address-book') &&
                      route.name !== 'NotFoundPage';
      return isPublic;
    });

    return publicRoutes;
  };

  const publicRoutes = getPublicRoutes();

  // Organize routes by category
  const mainPages = publicRoutes.filter(route => 
    ['LandingPage', 'SearchPage', 'SearchPageWithListingType'].includes(route.name)
  );

  const infoPages = publicRoutes.filter(route => 
    ['CMSPage', 'TermsOfServicePage', 'PrivacyPolicyPage'].includes(route.name)
  );

  const authPages = publicRoutes.filter(route => 
    ['LoginPage', 'SignupPage', 'ForgotPasswordPage'].includes(route.name)
  );

  const listingPages = publicRoutes.filter(route => 
    route.name.includes('Listing') && !mainPages.some(p => p.name === route.name)
  );

  const otherPages = publicRoutes.filter(route => 
    !mainPages.some(p => p.name === route.name) &&
    !infoPages.some(p => p.name === route.name) &&
    !authPages.some(p => p.name === route.name) &&
    !listingPages.some(p => p.name === route.name)
  );

  const renderRouteLink = (route) => {
    // Skip routes with dynamic parameters that we can't render without data
    if (route.path.includes(':')) {
      return null;
    }

    const routeName = route.name;
    let displayName = routeName;

    // Create user-friendly names
    const nameMap = {
      'LandingPage': intl.formatMessage({ id: 'SitemapPage.landingPage' }, { defaultMessage: 'Home' }),
      'SearchPage': intl.formatMessage({ id: 'SitemapPage.searchPage' }, { defaultMessage: 'Browse Listings' }),
      'LoginPage': intl.formatMessage({ id: 'SitemapPage.loginPage' }, { defaultMessage: 'Login' }),
      'SignupPage': intl.formatMessage({ id: 'SitemapPage.signupPage' }, { defaultMessage: 'Sign Up' }),
      'ForgotPasswordPage': intl.formatMessage({ id: 'SitemapPage.forgotPasswordPage' }, { defaultMessage: 'Forgot Password' }),
      'TermsOfServicePage': intl.formatMessage({ id: 'SitemapPage.termsPage' }, { defaultMessage: 'Terms of Service' }),
      'PrivacyPolicyPage': intl.formatMessage({ id: 'SitemapPage.privacyPage' }, { defaultMessage: 'Privacy Policy' }),
    };

    displayName = nameMap[routeName] || routeName.replace(/Page$/, '').replace(/([A-Z])/g, ' $1').trim();

    return (
      <li key={routeName} className={css.sitemapItem}>
        <NamedLink name={routeName} className={css.sitemapLink}>
          {displayName}
        </NamedLink>
      </li>
    );
  };

  const renderSection = (title, routes, messageId) => {
    if (routes.length === 0) return null;

    return (
      <div className={css.sitemapSection}>
        <H3 className={css.sectionTitle}>
          <FormattedMessage id={messageId} defaultMessage={title} />
        </H3>
        <ul className={css.sitemapList}>
          {routes.map(renderRouteLink).filter(Boolean)}
        </ul>
      </div>
    );
  };

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.staticPageWrapper}>
          <div className={css.contentWrapper}>
            <div className={css.content}>
              <H1 className={css.pageTitle}>
                <FormattedMessage id="SitemapPage.heading" defaultMessage="Site map" />
              </H1>

              <div className={css.description}>
                <FormattedMessage
                  id="SitemapPage.description"
                  defaultMessage="Find your way around our marketplace. Below are links to all the main sections and pages on our site."
                />
              </div>

              <div className={css.sitemapContainer}>
                {renderSection(
                  'Main Pages',
                  mainPages,
                  'SitemapPage.mainPagesTitle'
                )}

                {renderSection(
                  'Information Pages',
                  infoPages,
                  'SitemapPage.infoPagesTitle'
                )}

                {renderSection(
                  'Account Pages',
                  authPages,
                  'SitemapPage.accountPagesTitle'
                )}

                {renderSection(
                  'Other Pages',
                  otherPages,
                  'SitemapPage.otherPagesTitle'
                )}
              </div>

            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SitemapPageComponent.defaultProps = {
  currentUser: null,
};

SitemapPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: object,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser: state.user.currentUser,
  };
};

const SitemapPage = compose(
  connect(mapStateToProps),
  injectIntl
)(SitemapPageComponent);

export default SitemapPage;
